import { useState, useEffect, useCallback } from 'react';
import { Briefcase, MapPin, Calendar, Clock, PoundSterling, MessageSquare, X, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config/api';

/**
 * ConfirmedJobs — visible on the tradesperson AND employer dashboards.
 * Renders accepted offers as a card list with a click-through detail modal
 * that shows EVERY field the user requested:
 *   job title · description · full address · postcode · start date / time
 *   · duration · agreed day rate · employer/company name · contact CTA · notes.
 *
 * Reads from GET /api/employments (now returns rows for the current user
 * regardless of role). No credit consumption — pure read.
 */
export default function ConfirmedJobsSection({ userType }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openJob, setOpenJob] = useState(null);
  const navigate = useNavigate();

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/employments`, { withCredentials: true });
      // Newest first (backend already sorts) — keep confirmed/active states visible.
      setJobs((res.data || []).filter(j => ['confirmed', 'active', 'completed'].includes(j.status)));
    } catch (e) {
      // 403 swallowed — section just stays empty.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  if (loading) return null;
  if (!jobs.length) return null;

  const isEmployer = userType === 'employer';
  const titleLabel = isEmployer ? 'Confirmed Bookings' : 'Confirmed Jobs';

  return (
    <div className="card" data-testid="confirmed-jobs-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-emerald-500" />
          {titleLabel}
        </h3>
        <span className="text-xs text-slate-500" data-testid="confirmed-jobs-count">
          {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
        </span>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        {isEmployer
          ? 'Tradespeople you have hired. Tap a job for the full details.'
          : 'Jobs you have accepted. Tap a job for the full address and details.'}
      </p>

      <div className="space-y-3">
        {jobs.map(job => (
          <button
            key={job.employment_id}
            type="button"
            onClick={() => setOpenJob(job)}
            data-testid={`confirmed-job-${job.employment_id}`}
            className="w-full text-left bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 rounded-lg p-4 transition"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 truncate">
                  {job.job_title || job.trade || 'Job'}
                </p>
                <p className="text-sm text-slate-600 mt-0.5 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(job.start_date)}
                  {job.end_date && job.end_date !== job.start_date
                    ? ` → ${formatDate(job.end_date)}`
                    : ''}
                </p>
                <p className="text-sm text-slate-600 mt-0.5 flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {job.location_town || job.location_postcode}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-emerald-700">
                  {formatRate(job)}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isEmployer ? job.tradesperson_name : (job.employer_name || 'Employer')}
                </p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                  job.status === 'completed' ? 'bg-slate-200 text-slate-700' :
                  job.status === 'active' ? 'bg-blue-100 text-blue-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>
                  {job.status}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {openJob && (
        <ConfirmedJobModal
          job={openJob}
          isEmployer={isEmployer}
          onClose={() => setOpenJob(null)}
          onMessage={() => {
            const partnerId = isEmployer ? openJob.tradesperson_id : openJob.employer_id;
            navigate(`/messaging?with=${partnerId}`);
          }}
        />
      )}
    </div>
  );
}

function ConfirmedJobModal({ job, isEmployer, onClose, onMessage }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="confirmed-job-modal"
    >
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 truncate" data-testid="confirmed-job-title">
              {job.job_title || job.trade || 'Confirmed Job'}
            </h3>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
              job.status === 'completed' ? 'bg-slate-200 text-slate-700' :
              job.status === 'active' ? 'bg-blue-100 text-blue-700' :
              'bg-emerald-100 text-emerald-700'
            }`}>
              {job.status}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="confirmed-job-modal-close"
            className="p-2 hover:bg-slate-100 rounded-full"
          >
            <X className="h-5 w-5 text-slate-700" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {job.job_description && (
            <Field label="Description" testId="confirmed-job-description">
              <p className="text-slate-700 whitespace-pre-wrap">{job.job_description}</p>
            </Field>
          )}

          <Field label={isEmployer ? 'Tradesperson' : 'Employer'} icon={<Building2 className="h-4 w-4" />}>
            <p className="font-semibold text-slate-900" data-testid="confirmed-job-counterparty">
              {isEmployer ? (job.tradesperson_name || 'Tradesperson') : (job.employer_name || 'Employer')}
            </p>
            {!isEmployer && job.employer_company && job.employer_company !== job.employer_name && (
              <p className="text-sm text-slate-600">{job.employer_company}</p>
            )}
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Start date" icon={<Calendar className="h-4 w-4" />}>
              <p className="font-medium text-slate-900" data-testid="confirmed-job-start-date">
                {formatDate(job.start_date)}
              </p>
            </Field>
            {job.end_date && job.end_date !== job.start_date && (
              <Field label="End date" icon={<Calendar className="h-4 w-4" />}>
                <p className="font-medium text-slate-900">{formatDate(job.end_date)}</p>
              </Field>
            )}
            {(job.hours_start || job.hours_end) && (
              <Field label="Hours" icon={<Clock className="h-4 w-4" />}>
                <p className="font-medium text-slate-900" data-testid="confirmed-job-hours">
                  {job.hours_start || '—'} – {job.hours_end || '—'}
                  {job.breaks ? ` · breaks: ${String(job.breaks).toLowerCase()}` : ''}
                </p>
              </Field>
            )}
            <Field label="Duration" icon={<Clock className="h-4 w-4" />}>
              <p className="font-medium text-slate-900">
                {(job.duration_days || (job.duration_weeks ? Math.round(job.duration_weeks * 7) : 1))} day{(job.duration_days || 1) === 1 ? '' : 's'}
              </p>
            </Field>
            <Field label="Agreed rate" icon={<PoundSterling className="h-4 w-4" />}>
              <p className="font-bold text-emerald-700 text-lg" data-testid="confirmed-job-rate">
                {formatRate(job)}
              </p>
            </Field>
          </div>

          <Field label="Job address" icon={<MapPin className="h-4 w-4" />}>
            <div className="text-slate-800" data-testid="confirmed-job-address">
              {job.location_address && <p>{job.location_address}</p>}
              {job.location_address_line2 && <p>{job.location_address_line2}</p>}
              {(job.location_town || job.location_county) && (
                <p>{[job.location_town, job.location_county].filter(Boolean).join(', ')}</p>
              )}
              <p className="font-mono text-sm mt-1">{job.location_postcode}</p>
            </div>
          </Field>

          {job.experience_level && (
            <Field label="Experience required">
              <p className="text-slate-800">{job.experience_level}</p>
            </Field>
          )}

          {job.crew_size && job.crew_size > 1 && (
            <Field label="Crew size">
              <p className="text-slate-800">{job.crew_size}</p>
            </Field>
          )}

          {job.notes && job.notes !== job.job_description && (
            <Field label="Notes from offer" testId="confirmed-job-notes">
              <p className="text-slate-700 whitespace-pre-wrap">{job.notes}</p>
            </Field>
          )}

          <div className="pt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={onMessage}
              data-testid="confirmed-job-message-btn"
              className="w-full sm:w-auto px-5 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg flex items-center justify-center gap-2"
            >
              <MessageSquare className="h-4 w-4" />
              Message {isEmployer ? 'tradesperson' : 'employer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon, children, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
        {icon}{label}
      </p>
      {children}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatRate(job) {
  if (job.rate_type === 'OPEN_TO_OFFERS') return 'Open to offers';
  if (job.day_rate) return `£${Number(job.day_rate).toFixed(0)}/day`;
  return '—';
}

// Allow caller to refresh after acceptance
export function useRefreshConfirmedJobs() {
  return () => {
    // Hook reserved for future websocket integration; for now the component
    // refetches on its own first mount. Components consuming this can call
    // window.dispatchEvent(new Event('confirmed-jobs:refresh')) and we'll
    // listen here when needed.
  };
}
